import { useMemo, useState } from "react";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Callout,
  Card,
  CopyButton,
  Empty,
  ErrorState,
  Field,
  Loading,
  PageIntro,
  formatDateTime,
  formatNumber,
  useToast,
} from "../components/ui.jsx";
import { IconToken, IconWarning } from "../components/icons.jsx";

// Mirrors MaxVendableEnergyKWh in tokenservice/token.go. The server is
// authoritative; this only stops the operator typing a figure that would be
// refused, and explains why before they press the button.
const MAX_ENERGY_KWH = 1500;

export default function Vending() {
  const toast = useToast();
  const actions = useAsync(() => api.tokenActions(), []);
  const devices = useAsync(() => api.listDevices({ state: "ACTIVE", limit: 500 }), []);

  const [deviceNumber, setDeviceNumber] = useState("");
  const [channel, setChannel] = useState(0);
  const [actionKey, setActionKey] = useState("0:0");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  const device = (devices.data ?? []).find((d) => d.Number === deviceNumber);
  const usable = useMemo(
    () => (actions.data ?? []).filter((a) => !a.reserved),
    [actions.data]
  );
  const action = usable.find((a) => `${a.class}:${a.subclass}` === actionKey);
  const takesAmount = action && action.amount !== "none";

  // The head-end deals only in energy. It holds no tariffs and no customer
  // rates, so a credit amount here is kilowatt-hours and nothing else.
  const isEnergyCredit = takesAmount && action.amount === "kWh";
  const overCeiling = isEnergyCredit && amount !== "" && Number(amount) > MAX_ENERGY_KWH;
  const energyCeilingMessage =
    `A single token carries at most ${formatNumber(MAX_ENERGY_KWH, 0)} kWh exactly. ` +
    "Above that the meter would credit a rounded figure, so issue the rest as a second token.";

  const history = useAsync(
    () => (deviceNumber ? api.vendHistory(deviceNumber, 20) : Promise.resolve([])),
    [deviceNumber]
  );

  async function vend(event) {
    event.preventDefault();
    if (!device || !action || overCeiling) return;
    setBusy(true);
    setIssued(null);
    try {
      const result = await api.vend({
        device: device.Number,
        channel: Number(channel) || 0,
        class: action.class,
        subclass: action.subclass,
        amount: takesAmount ? Number(amount) : 0,
        reference: reference.trim(),
      });
      setIssued(result);
      history.reload();
      if (result.replayed) {
        toast.info(
          "Existing token returned",
          "This reference was already used, so the original token was returned rather than a second one being created."
        );
      } else {
        toast.success("Token issued", `${action.name} for ${device.Number}.`);
      }
    } catch (err) {
      toast.error("The token was not issued", err.message);
    } finally {
      setBusy(false);
    }
  }

  if (actions.loading || devices.loading) return <Loading label="Loading vending options" />;
  if (actions.error) return <ErrorState error={actions.error} onRetry={actions.reload} />;

  return (
    <>
      <PageIntro title="Issue a prepaid token">
        This is a last resort. Vending belongs in the management system, which holds the
        tariffs and the customer rates; the head-end holds neither, so it can only credit
        energy. Use this when the management system is unreachable, or when a device has
        no radio link and the customer needs twenty digits to type into the keypad.
        Anything else should be sent as a command instead.
      </PageIntro>

      <div className="grid grid-2">
        <Card title="New token" hint="The token is recorded before it is shown to you">
          <form onSubmit={vend}>
            <Field label="Device" required hint="Only devices in service can be vended against.">
              <select
                className="select"
                value={deviceNumber}
                onChange={(e) => {
                  setDeviceNumber(e.target.value);
                  setChannel(0);
                  setIssued(null);
                }}
                required
              >
                <option value="">Choose a device</option>
                {(devices.data ?? [])
                  .filter((d) => d.Kind !== "NOVALINK")
                  .map((d) => (
                    <option key={d.Number} value={d.Number}>
                      {d.Number} · {d.ModelCode}
                      {d.ChannelCount > 1 ? ` (${d.ChannelCount}-in-1)` : ""}
                    </option>
                  ))}
              </select>
            </Field>

            {device && device.ChannelCount > 1 && (
              <Field
                label="Channel"
                required
                hint="Each circuit has its own key and balance. Choosing the wrong one credits the wrong customer."
              >
                <select
                  className="select"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <option value={0}>Whole unit</option>
                  {Array.from({ length: device.ChannelCount }, (_, i) => i + 1).map((i) => (
                    <option key={i} value={i}>
                      Channel {i}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label="Action"
              required
              hint="Reserved slots are hidden; they exist in the wire format but must never be issued."
            >
              <select
                className="select"
                value={actionKey}
                onChange={(e) => {
                  setActionKey(e.target.value);
                  setAmount("");
                  setIssued(null);
                }}
                required
              >
                {usable.map((a) => (
                  <option key={`${a.class}:${a.subclass}`} value={`${a.class}:${a.subclass}`}>
                    {a.name}
                    {a.amount !== "none" ? ` (${a.amount})` : ""}
                  </option>
                ))}
              </select>
            </Field>

            {takesAmount && (
              <Field
                label={amountLabel(action.amount)}
                required
                error={overCeiling ? energyCeilingMessage : null}
                hint={
                  action.whole_number
                    ? "A whole number. Fractions are rejected."
                    : isEnergyCredit
                    ? `Up to ${formatNumber(MAX_ENERGY_KWH, 0)} kWh per token, in steps of 0.1 kWh.`
                    : "Amounts above 1,638.3 are rounded to the nearest value the token can carry."
                }
              >
                <input
                  className="input"
                  type="number"
                  min="0"
                  max={isEnergyCredit ? MAX_ENERGY_KWH : undefined}
                  step={action.whole_number ? 1 : "0.1"}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </Field>
            )}

            <Field
              label="Payment reference"
              hint="Recommended. Re-submitting the same reference returns the original token instead of creating a second one, so a retried payment cannot double-credit a customer."
            >
              <input
                className="input mono"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. PAY-2026-08-04-0192"
              />
            </Field>

            <button
              className="btn btn-primary btn-block"
              disabled={busy || !device || !action || overCeiling || (takesAmount && amount === "")}
            >
              {busy ? <span className="spinner" /> : <IconToken />}
              {busy ? "Issuing" : "Issue token"}
            </button>
          </form>

          {issued && (
            <div style={{ marginTop: 20 }}>
              <Callout kind={issued.replayed ? "info" : "success"}>
                {issued.replayed
                  ? "This reference had already been used. The original token is shown below."
                  : "Token issued and recorded. Give these digits to the customer."}
              </Callout>
              <div className="secret-reveal">
                <span>{issued.token}</span>
                <CopyButton value={issued.token} />
              </div>
              <p className="hint" style={{ marginTop: 8 }}>
                Applied amount: {formatNumber(issued.issue.Amount)} · Transfer ID{" "}
                {issued.issue.TransferID} · Key revision #{issued.issue.KeyRevision}
              </p>
            </div>
          )}
        </Card>

        <Card
          title="Recent tokens"
          hint={deviceNumber ? `For ${deviceNumber}` : "Choose a device to see what was issued"}
        >
          {!deviceNumber ? (
            <Empty title="No device selected">
              Every token ever issued is recorded. Pick a device to review its history.
            </Empty>
          ) : history.loading ? (
            <Loading />
          ) : (history.data ?? []).length === 0 ? (
            <Empty title="Nothing issued yet">
              No tokens have been issued for this device.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Amount</th>
                    <th>Issued</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((v) => (
                    <tr key={v.ID}>
                      <td className="mono">{v.Token}</td>
                      <td>{formatNumber(v.Amount)}</td>
                      <td className="hint">{formatDateTime(v.IssuedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Callout kind="warning">
            <strong>A clawback is not a negative amount.</strong> The token's amount field
            is unsigned, so use the <em>Credit Deduct</em> action to reverse an over-vend.
          </Callout>
        </Card>
      </div>
    </>
  );
}

function amountLabel(kind) {
  return (
    {
      kWh: "Amount (kWh)",
      watts: "Power limit (watts)",
      days: "Service days",
      KRN: "New key revision number",
    }[kind] ?? "Amount"
  );
}
